import { Component } from 'preact';
import {
  DIALOG_STYLE, FourCorners,
  TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED,
  ACCENT_GOLD, GOLD, GOLD_BTN,
} from '../../lib/medievalTheme';
import { t } from '../../localization/t';

/**
 * Minimal two-stage mulligan banner shown after the opening draw.
 *
 * Stage 1. PROMPT: a tiny "Mulligan?" pill with Keep / Mulligan
 * buttons. Most players know what a mulligan is; they just need to
 * say yes or no. No explanatory copy, no modal takeover.
 *
 * Stage 2. SELECTING: after the player opts in, a compact counter
 * + Cancel / Mulligan-N pair appears. Hand-card clicks toggle the
 * mulligan flag; selected cards get a warm-gold highlight reusing
 * the style of table-card selection.
 *
 * Position: top-center, translucent background so the board stays
 * visible behind it. The shake() method is load-bearing. GameBoard
 * calls it when the player tries to flag a 4th card past the cap.
 */
export default class MulliganOverlay extends Component {
  componentWillUnmount() {
    if (this._shakeTimer) clearTimeout(this._shakeTimer);
  }

  shake = () => {
    if (this._shakeTimer) clearTimeout(this._shakeTimer);
    this.setState({ shaking: true });
    this._shakeTimer = setTimeout(() => this.setState({ shaking: false }), 380);
  };

  render() {
    const { stage, selectedCount, maxCount, onAccept, onConfirm, onKeep } = this.props;
    const shaking = this.state?.shaking;

    const containerStyle = {
      position: 'fixed',
      // Sits just above the player's hand. The hand fan tops out at
      // roughly 220px from the bottom (cards at y=-5 when hovered +
      // 175px card height). Anchoring here keeps the prompt close
      // to the cards it's asking about, instead of forcing the user
      // to look up to the top of the screen.
      bottom: '15cqw',
      // Sit centred in the right third of the viewport. Splitting the
      // screen into thirds gives a midpoint of 5/6 ≈ 83.333% on the
      // x axis; translateX(-50%) then centres the pill on that point.
      left: '83.3333%',
      transform: 'translateX(-50%)',
      zIndex: 1050,
      padding: '0.625cqw 0.875cqw',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.75cqw',
      animation: shaking
        ? 'mulligan-shake 0.38s cubic-bezier(.36,.07,.19,.97)'
        : 'mulligan-fade-in 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
      ...DIALOG_STYLE,
      // Slightly reduced chrome vs the big dialog so the pill reads
      // as a secondary control, not a blocking modal.
      borderRadius: 'var(--radius-xl)',
    };

    return (
      <div style={containerStyle}>
        <FourCorners />
        {stage === 'prompt' ? this.renderPrompt() : this.renderSelecting()}
        <style>{`
          @keyframes mulligan-fade-in {
            from { opacity: 0; transform: translateX(-50%) translateY(-0.375cqw); }
            to   { opacity: 1; transform: translateX(-50%) translateY(0); }
          }
          @keyframes mulligan-shake {
            0%, 100% { transform: translateX(-50%); }
            15%, 45%, 75% { transform: translateX(calc(-50% - 0.3125cqw)); }
            30%, 60%, 90% { transform: translateX(calc(-50% + 0.3125cqw)); }
          }
        `}</style>
      </div>
    );
  }

  renderPrompt() {
    const { onAccept, onKeep } = this.props;
    return (
      <>
        <span style={labelStyle}>{t('Mulligan?')}</span>
        <button type="button" style={ghostButtonStyle} onClick={onKeep} title={t('Keep your opening hand (N / K)')}>
          {t('Keep')} <span style={kbdStyle}>N</span>
        </button>
        <button type="button" style={primaryButtonStyle} onClick={onAccept} title={t('Pick cards to return (Y)')}>
          {t('Mulligan')} <span style={kbdStylePrimary}>Y</span>
        </button>
      </>
    );
  }

  renderSelecting() {
    const { selectedCount, maxCount, onConfirm, onKeep } = this.props;
    const canConfirm = selectedCount > 0;
    return (
      <>
        <span
          style={{
            ...labelStyle,
            color: selectedCount > 0 ? ACCENT_GOLD : TEXT_MUTED,
          }}
        >
          {t('{n} / {max} selected', { n: selectedCount, max: maxCount })}
        </span>
        <button type="button" style={ghostButtonStyle} onClick={onKeep} title={t('Cancel. Keep current hand (K / Esc)')}>
          {t('Cancel')} <span style={kbdStyle}>K</span>
        </button>
        <button
          type="button"
          disabled={!canConfirm}
          style={{ ...primaryButtonStyle, opacity: canConfirm ? 1 : 0.45 }}
          onClick={canConfirm ? onConfirm : undefined}
          title={canConfirm ? t('Commit the mulligan (M)') : t('Select at least one card first')}
        >
          {/* Reserve space for the `(N)` suffix in every state. When
 nothing is selected we render a hidden placeholder sized
 to the maximum count so the pill never grows or wraps
 when the count appears. */}
 <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: '0.375cqw', whiteSpace: 'nowrap' }}>
 <span>{t('Confirm')}</span>
 <span
 style={{
 display: 'inline-block',
 minWidth: '2.4em',
 textAlign: 'left',
 fontVariantNumeric: 'tabular-nums',
 visibility: canConfirm ? 'visible' : 'hidden',
 }}
 >
 ({canConfirm ? selectedCount : maxCount})
 </span>
 </span>
 <span style={kbdStylePrimary}>M</span>
 </button>
 </>
 );
 }
}

/* ── styles ───────────────────────────────────────────────────── */

const labelStyle = {
 fontFamily: "'Geist Variable', sans-serif",
 fontSize: '0.6875cqw',
 fontWeight: 600,
 color: TEXT_PRIMARY,
 padding: '0 0.375cqw',
};

const ghostButtonStyle = {
 padding: '0.4375cqw 0.875cqw',
 borderRadius: 'var(--radius-md)',
 fontFamily: "'Geist Variable', sans-serif",
 fontSize: '0.6875cqw',
 fontWeight: 600,
 background: 'transparent',
 color: TEXT_BODY,
 border: `1px solid ${GOLD} 0.35)`,
 display: 'inline-flex',
 alignItems: 'center',
 gap: '0.5cqw',
};

const primaryButtonStyle = {
 padding: '0.4375cqw 1cqw',
 borderRadius: 'var(--radius-md)',
 fontFamily: "'Geist Variable', sans-serif",
 fontSize: '0.6875cqw',
 fontWeight: 700,
 display: 'inline-flex',
 alignItems: 'center',
 gap: '0.5cqw',
 transition: 'transform 0.15s',
 // Never wrap the label or shrink the pill when the selected-count
 // swap-in changes the visible string.
 whiteSpace: 'nowrap',
 flexShrink: 0,
 ...GOLD_BTN,
};

const kbdStyle = {
 display: 'inline-block',
 padding: '0.0625cqw 0.3125cqw',
 border: `1px solid ${GOLD} 0.4)`,
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.5625cqw',
  letterSpacing: 0,
  color: TEXT_MUTED,
  fontWeight: 500,
};

const kbdStylePrimary = {
  ...kbdStyle,
  color: 'rgba(30, 20, 8, 0.7)',
  border: '1px solid rgba(30, 20, 8, 0.3)',
};
